import React, { useEffect, useState, useCallback } from "react"
import { Course, Subject } from "../../../../types/dataModel"
import useFetch from "../../../../hooks/useFetch"
import { API, DIFFICULTY_LEVELS } from "../../../../common/Constants"
import PreLoader from "../../../../core/common/preLoader"
import { getAPIHeaders, isNotEmpty } from "../../../../common/Utils"
import CommonSelect from "../../../../core/common/commonSelect"
import { useAccess } from "../../../../hooks/accessContext"
import CustomModal from "../../../../core/common/customModal"
import CommonSelectV2 from "../../../../core/common/commonSelectV2"

interface Props {
    course: Course
    onSuccess: ()=> void
}

const ModifyCourse = ({course, onSuccess}:Props) => {
    const {hasAcademicMgntEdit} = useAccess()
    const [formData, setFormData] = useState<Course | undefined>(course)
    const [courseCategories, setCourseCategories] = useState<any[]>([])
    const [msgBox, setMsgBox] = useState<any>({
      msg: "",
      error: false
    });
    const {isLoading, error, callFetch } = useFetch();
    const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
    const [learningHighlights, setLearningHighlights] = useState<string[]>([]);
    const [subjectList, setSubjectList] = useState<Subject[]>([]);
    const [selectedSubjects, setSelectedSubjects] = useState<any[]>([]);

    useEffect(()=>{
      setFormData(course)
      if(course?.metadata?.learning_highlights){
        setLearningHighlights(course.metadata.learning_highlights)
      } else {
        setLearningHighlights([])
      }
      if(course?.subjects){
        let subjectCodes:any = course?.subjects.map((ele)=> ele.code)
        if(subjectCodes?.length > 0){
          setSelectedSubjects(subjectCodes)
        }
      } else {
        setSelectedSubjects([])
      }
    },[course])

    const fetchCourseCategories = useCallback(async () => {
        try {
            const response = await callFetch(API.SETTINGS_COURSE_CATEGORIES, 'GET', getAPIHeaders(), null, null);
            if (response) {
                setCourseCategories(response.map((ele:any)=>{
                  return {
                    value: ele.name,
                    label: ele.name
                  }
                }));
            } else {
              setCourseCategories([]);
            }
        } catch (err) {
            console.error("Failed to fetch course categories:", err);
        }
    }, []);

    useEffect(() => {
      fetchCourseCategories();
      fetchSubjects();
    }, []);

    const fetchSubjects = useCallback(async () => {
        try {
            const response = await callFetch(API.SUBJECT_SEARCH, 'GET', getAPIHeaders(), null, null);
            if (response) {
                setSubjectList(response);
            } else {
              setSubjectList([]);
            }
        } catch (err) {
            console.error("Failed to fetch subjects:", err);
        }
    }, []);

    const addLearningHighlight = () => {
      const updatedHighlights = [...learningHighlights, ""];
      setLearningHighlights(updatedHighlights);
      setFormData({
        ...formData,
        metadata: {
          ...formData?.metadata,
          learning_highlights: updatedHighlights,
        },
      });
    };

    const removeLearningHighlight = (index: number) => {
      const updatedHighlights = learningHighlights.filter((_, i) => i !== index);
      setLearningHighlights(updatedHighlights);
      setFormData({
        ...formData,
        metadata: {
          ...formData?.metadata,
          learning_highlights: updatedHighlights,
        },
      });
    };

    const reset = () => {
      resetMsgBox()
      setShowPreviewModal(false)
    }

    const resetMsgBox = () => {
      setMsgBox({
        msg: "",
        error: false
      })
    }
        
        
    useEffect(() => {
            if(error){
              if(error.response?.data){
                setMsgBox({msg: error.response?.data, error: true})
              } else{
                setMsgBox({msg: 'Something went wrong please try again later.', error: true})
              }
            }
    }, [error]);
        
    const renderMsgBox = () => {
      return (
        <div>
        { msgBox?.msg  
            ?
            msgBox?.error
            ?
              <>
                <i className="ti ti-alert-triangle text-danger" />  
                <span className='text-danger'> {msgBox?.msg} </span> 
              </> 
            :
              <>
                <i className="ti ti-thumb-up text-success" />  
                <span className='text-success'> {msgBox?.msg} </span> 
              </> 
            : <></>
        }
      </div>
      )
    }

    const handleSubmit = (event:any) => {
          event.preventDefault()
          if(!isNotEmpty(formData?.title)){
            setMsgBox({msg: "Please provide title", error: true})
          } else if(!isNotEmpty(formData?.description)){
            setMsgBox({msg: "Please provide description", error: true})
          } else if(!isNotEmpty(formData?.code)){
            setMsgBox({msg: "Please provide code", error: true})
          } else if(!isNotEmpty(formData?.type)){
            setMsgBox({msg: "Please select type", error: true})
          } else {
            setMsgBox({msg:"",error: false})
            callFetch(API.COURSE,
                  'PUT',
                  getAPIHeaders(),
                  null,
                  {
                     ...formData,
                     metadata: {
                       ...formData?.metadata,
                       learning_highlights: learningHighlights,
                     }
                  }
              ).then(()=>{
                setMsgBox({msg: "Updated successfully", error: false})
                onSuccess()
              }).catch((err)=>{
                setMsgBox({msg: err.response?.data || "Something went wrong, please try again later.", error: true});
              });
          }
    }

    const handleSaveSubjects = (event:any) => {
      event.preventDefault()
      if(!isNotEmpty(formData?.code)){
        setMsgBox({msg: "Please provide course code", error: true})
        return
      }

      setMsgBox({msg:"",error: false})
      
      // Get current subject codes from course
      const currentSubjectCodes = course?.subjects?.map((sub) => sub.code) || []
      
      // Find subjects to add (in selected but not in current)
      const subjectsToAdd = selectedSubjects.filter((code) => !currentSubjectCodes.includes(code))
      
      // Find subjects to remove (in current but not in selected)
      const subjectsToRemove = currentSubjectCodes.filter((code) => !selectedSubjects.includes(code))
      
      // Create promises for all operations
      const addPromises = subjectsToAdd.map((subjectCode) => 
        callFetch(API.COURSE_SUBJECT,
          'POST',
          getAPIHeaders(),
          null,
          {
            courseCode: formData?.code,
            subjectCode: subjectCode
          }
        )
      )
      
      const removePromises = subjectsToRemove.map((subjectCode) => 
        callFetch(API.COURSE_SUBJECT,
          'DELETE',
          getAPIHeaders(),
          null,
          {
            courseCode: formData?.code,
            subjectCode: subjectCode
          }
        )
      )
      
      // Execute all operations
      Promise.all([...addPromises, ...removePromises]).then(()=>{
        setMsgBox({msg: "Updated successfully", error: false})
        onSuccess()
      }).catch((err)=>{
        setMsgBox({msg: err.response?.data || 'Failed to update subjects. Please try again.', error: true});
      });
    }

    const renderBasicInfoForm = () => {
      return (
        <div
          className="tab-pane fade show active" 
          id="modify-basic-info"
          role="tabpanel"
          aria-labelledby="modify-info-tab"
          tabIndex={0}
        >
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Title <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control" 
                  value={formData?.title}
                  onChange={(e)=>{
                    setFormData({
                      ...formData,
                      title: e.target.value
                    })
                  }}
                  disabled={!hasAcademicMgntEdit()}/>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control"
                      value={formData?.description}
                      onChange={(e)=>{
                          setFormData({
                              ...formData,
                              description: e.target.value
                          })
                      }} 
                      disabled={!hasAcademicMgntEdit()}/>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Code <span className="text-danger"> *</span></label>
                  <input type="text" className="form-control"
                      value={formData?.code}
                      onChange={(e)=>{
                        setFormData({
                              ...formData,
                              code: e.target.value
                          })
                      }} disabled/>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Category <span className="text-danger"> *</span></label>
                  <CommonSelect
                        disabled={!hasAcademicMgntEdit()}
                        className='select'
                        options={courseCategories}
                        defaultValue={(() => {
                          const foundCategory = courseCategories.find((category: any) => category.value === course?.type);
                          if (foundCategory) {
                            return foundCategory;
                          }
                          return null;
                        })()}
                        onChange={(value)=>{
                          setFormData({
                              ...formData,
                              type: value
                          })
                    }}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isCommercial"
                      checked={formData?.isCommercial || false}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          isCommercial: e.target.checked,
                        });
                      }}
                      disabled={!hasAcademicMgntEdit()}
                    />
                    <label className="form-check-label" htmlFor="isCommercial">
                      Commercial Course
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ 
              justifyContent: "space-between",
              flexDirection:'row'}}>
             {renderMsgBox()}
              <div>
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={reset}
                >
                  Cancel
                </button>
                <button type="button"
                  disabled={!hasAcademicMgntEdit()}
                  className="btn btn-primary"
                  onClick={handleSubmit}>
                  Save Course
                </button>
            </div>
          </div>
        </div>
      )
    }

    const renderSubjectsForm = () => {
      return (
        <div
          className="tab-pane fade"
          id="modify-subjects-info"
          role="tabpanel"
          aria-labelledby="modify-subjects-tab"
          tabIndex={0}
        >
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Subjects <span className="text-danger"> *</span></label>
                  <CommonSelectV2
                    isMulti
                    className='select'
                    options={parseSubjectsToOptions(subjectList)}
                    values={parseSubjectsToOptions(subjectList).filter(
                      (option) => selectedSubjects.includes(option.value)
                    )}
                    onChange={(value) => {
                      setSelectedSubjects(value)
                    }}
                    disabled={!hasAcademicMgntEdit()}
                    formatOptionLabel={(option: any) => (
                      <div>
                        <h6 className="d-flex align-items-center">
                          {option.label}
                          <span className={`ms-2 badge badge-primary rounded-pill`}>
                            {option.value}
                          </span>
                        </h6>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ 
              justifyContent: "space-between",
              flexDirection:'row'}}>
             {renderMsgBox()}
              <div>
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={reset}
                >
                  Cancel
                </button>
                <button type="button"
                  disabled={!hasAcademicMgntEdit()}
                  className="btn btn-primary"
                  onClick={handleSaveSubjects}>
                  Save Subjects
                </button>
            </div>
          </div>
        </div>
      )
    }

    const renderMetadataForm = () => {
      return (
        <div
          className="tab-pane fade"
          id="modify-metadata-info"
          role="tabpanel"
          aria-labelledby="modify-metadata-tab"
          tabIndex={0}
        >
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Thumbnail URL</label>
                  <div className="input-group">
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://example.com/thumbnail.jpg"
                      value={formData?.metadata?.thumbnail_url || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          metadata: {
                            ...formData?.metadata,
                            thumbnail_url: e.target.value,
                          },
                        });
                      }}
                      disabled={!hasAcademicMgntEdit()}
                    />
                    {formData?.metadata?.thumbnail_url && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPreviewModal(true)}
                        title="Preview Thumbnail"
                        disabled={!hasAcademicMgntEdit()}
                        style={{ zIndex: 0 }}
                      >
                        <i className="ti ti-eye" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 bg-light-500 rounded p-2">
                     <small className="text-info">Note: Thumbnail should be in 16:9 ratio.</small>
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Difficulty Level</label>
                  <CommonSelectV2
                    options={DIFFICULTY_LEVELS}
                    defaultValue={DIFFICULTY_LEVELS.find((item) => item.value === formData?.metadata?.difficulty_level) || DIFFICULTY_LEVELS[0]}
                    value={formData?.metadata?.difficulty_level ? { value: formData.metadata.difficulty_level, label: formData.metadata.difficulty_level } : null}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        metadata: {
                          ...formData?.metadata,
                          difficulty_level: value,
                        },
                      });
                    }}
                    placeHolder="Select Difficulty Level"
                    disabled={!hasAcademicMgntEdit()}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Learning Highlights</label>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addLearningHighlight}
                      disabled={!hasAcademicMgntEdit()}
                    >
                      <i className="ti ti-plus" />
                    </button>
                  </div>
                  {learningHighlights.map((highlight, index) => (
                    <div key={index} className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter a learning highlight"
                        value={highlight}
                        onChange={(e) => {
                          const updatedHighlights = [...learningHighlights];
                          updatedHighlights[index] = e.target.value;
                          setLearningHighlights(updatedHighlights);
                          setFormData({
                            ...formData,
                            metadata: {
                              ...formData?.metadata,
                              learning_highlights: updatedHighlights,
                            },
                          });
                        }}
                        disabled={!hasAcademicMgntEdit()}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeLearningHighlight(index)}
                        disabled={!hasAcademicMgntEdit()}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ 
              justifyContent: "space-between",
              flexDirection:'row'}}>
             {renderMsgBox()}
              <div>
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={reset}
                >
                  Cancel
                </button>
                <button type="button"
                  disabled={!hasAcademicMgntEdit()}
                  className="btn btn-primary"
                  onClick={handleSubmit}>
                  Save Course
                </button>
            </div>
          </div>
        </div>
      )
    }

    const renderForm = () => {
      return (
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Modify Course</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={reset}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form>
            <div className="contact-grids-tab">
              <ul className="nav nav-underline" id="myTab" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="modify-info-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#modify-basic-info"
                    type="button"
                    role="tab"
                    aria-selected="true"
                    onClick={resetMsgBox}
                  >
                    Basic Information
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="modify-subjects-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#modify-subjects-info"
                    type="button"
                    role="tab"
                    aria-selected="false"
                    onClick={resetMsgBox}
                  >
                    Subjects
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="modify-metadata-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#modify-metadata-info"
                    type="button"
                    role="tab"
                    aria-selected="false"
                    onClick={resetMsgBox}
                  >
                    Metadata
                  </button>
                </li>
              </ul>
            </div>
            <div className="tab-content" id="myTabContent">
              {renderBasicInfoForm()}
              {renderSubjectsForm()}
              {renderMetadataForm()}
            </div>
          </form>
        </div>
      )
    }

    const renderPreviewModal = () => {
      return (
        <CustomModal
          show={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title="Thumbnail Preview"
          size="lg"
          bodyContent={
            <div className="text-center">
              <img
                src={formData?.metadata?.thumbnail_url}
                alt="Thumbnail Preview"
                className="img-fluid rounded"
                style={{ maxHeight: "400px", maxWidth: "100%" }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const errorDiv = document.createElement("div");
                  errorDiv.className = "alert alert-warning";
                  errorDiv.textContent = "Failed to load image. Please check the URL.";
                  target.parentNode?.appendChild(errorDiv);
                }}
              />
              <div className="mt-3">
                <small className="text-muted">
                  URL: {formData?.metadata?.thumbnail_url}
                </small>
              </div>
            </div>
          }
          footerContent={
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowPreviewModal(false)}
            >
              Close
            </button>
          }
        />
      );
    };

    return (
      <>
        <div className="modal fade" id="edit_course"
          data-bs-backdrop="static"
          data-bs-keyboard="false">
          <PreLoader showLoader={isLoading} />
          <div className="modal-dialog modal-dialog-centered modal-md"
            >
            {renderForm()}
          </div>
        </div>
        {renderPreviewModal()}
      </>
    )
}

function parseSubjectsToOptions (data: Subject[]) {
  let opts: any[] = []
  if(data){
    data.forEach((ele)=> opts.push({
      value: ele.code,
      label: ele.title
    }))
  }
  return opts;
}

export default ModifyCourse